import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

export default function SnapTest({ user }) {
  // ------- Endpoints -------
  const BASE_URL = process.env.REACT_APP_API_URL;

  // endpoints
  const [pythonUrl, setPythonUrl] = useState(
    `${BASE_URL}api/snap/decks` // if your backend proxies python helper, otherwise remove
  );

  const ADD_DECK_URL = `${BASE_URL}api/addSnapDeck`;
  const UPDATE_DECK_PUT = (id) => `${BASE_URL}api/updateSnapDeck/${id}`;
  const UPDATE_DECK_POST = `${BASE_URL}api/updateSnapDeck`;

  // match your backend: it has /snapDecks not /snapAllDecks
  const ALL_DECKS_URL = `${BASE_URL}api/data/snapDecks/user/${user.id}`;
  const ALL_CARDS_URL = `${BASE_URL}api/data/snapAllCards`;

  // helper endpoint — if your backend exposes this
  const FROM_PATH_URL = `${BASE_URL}api/snap/fromPath`;
  const DEFAULT_WINDOWS_PATH =
    'C:\\Users\\cjtob\\AppData\\LocalLow\\Second Dinner\\SNAP\\Standalone\\States\\nvprod\\CollectionState.json';

  // ------- Data state -------
  const [loading, setLoading] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingDecks, setLoadingDecks] = useState(false);

  const [pythonDecks, setPythonDecks] = useState([]);
  const [snapCards, setSnapCards] = useState([]);
  const [existingDecks, setExistingDecks] = useState([]);

  const [rows, setRows] = useState([]);
  const [importingAll, setImportingAll] = useState(false);
  const [logs, setLogs] = useState([]);
  const [userRanks, setUserRanks] = useState([]);

  // UI: full-screen overlay panel
  const [panelOpen, setPanelOpen] = useState(false);

  // Fallback picker if fixed-path helper fails
  const fileInputRef = useRef(null);

  // ------- Helpers -------
  const log = (msg) => setLogs((prev) => [msg, ...prev].slice(0, 300));
  const stripBOM = (s) => (s && s.charCodeAt(0) === 0xfeff ? s.slice(1) : s);

 // Extractor (raw CollectionState.json)
const extractDecksFromJsonData = (data) => {
  const decks = (data?.ServerState?.Decks ?? []);
  const out = [];
  for (const d of decks) {
    const name = d?.Name ?? '(Unnamed Deck)';
    const cardBackDefId = d?.CardBack?.CardBackDefId || '';
    let names = [];
    const cardsBlock = d?.Cards ?? [];
    if (Array.isArray(cardsBlock) && cardsBlock.length && typeof cardsBlock[0] === 'object') {
      names = cardsBlock.map((c) => c?.CardDefId ?? '');
    } else {
      names = d?.CardIds ?? [];
    }
    if (names.length < 12) names = names.concat(Array(12 - names.length).fill(''));
    names = names.slice(0, 12);

    out.push({
      deckName: name,
      cards: names,
      cardBackDefId,
      rank: "The Rookies",   // Always The Rookies
    });
  }
  return out;
};

  const toUpperCamel = (s) => {
    return String(s || "")
      .normalize("NFD")                    // remove accents
      .replace(/[^a-zA-Z0-9\s]/g, " ")     // replace punctuation with space
      .split(/\s+/)                        // split on spaces
      .filter(Boolean)                     // remove empties
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("");
  };

  // console.log(toUpperCamel("Squirrel girl"));

  const coerceToDeckArray = (payload) => {
  if (Array.isArray(payload) && payload.length) {
    const looksNormalized = payload.every(
      (x) =>
        (typeof x?.deckName === 'string' || typeof x?.name === 'string' || typeof x?.title === 'string') &&
        (Array.isArray(x?.cards) || Array.isArray(x?.cardNames) || Array.isArray(x?.list))
    );
    if (looksNormalized) {
      return payload.map((d) => {
        const cardBackDefId =
          d.cardBackDefId ?? d.cardBack ?? d.CardBackDefId ?? (d.CardBack && d.CardBack.CardBackDefId) ?? '';
        return {
          deckName: d.deckname ?? d.name ?? d.title ?? '(Unnamed Deck)',
          cards: (d.cards ?? d.cardNames ?? d.list ?? []).slice(0, 12),
          cardBackDefId,
          rank: "The Rookies",   // Always The Rookies
        };
      });
    }
  }
  if (payload && Array.isArray(payload.decks)) return coerceToDeckArray(payload.decks);
  if (payload?.ServerState?.Decks) return extractDecksFromJsonData(payload);
  return [];
};


  const normalizeName = (s) => {
    const n = String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return n || '(unnamed)';
  };
  const splitDeckName = (full) => {
    const m = String(full || '').match(/\s+v(\d+(?:\.\d+)*)$/i);
    if (!m) {
      return { base: String(full || '').trim(), versionNum: "-" };
    }
    const versionNum = m[1]; // only digits/decimals, no "v"
    const base = String(full || '').slice(0, m.index).trim();
    return { base, versionNum };
  };

  const ALIASES = useMemo(() => new Map([
    ["Hood", "TheHood"],
    ["Ronan", "RonanTheAccuser"],
    ["KateBishop", "HawkeyeKateBishop"],
    ["Juggernaut,HorsemanOfWar", "JuggernautHorseman"],
    ["SamWilson", "SamWilsonCaptainAmerica"],
    ["Doom2099", "DoctorDoom2099"],
    ["Arana", "AranA"],
    ["MrNegative", "MisterNegative"],
    ["KarimaShapandar", "OmegaSentinel"],
    ["StarlordMasterOfTheSun", "StarLordMasterOfTheSun"],
    ["MrFantastic", "MisterFantastic"],
    ["Modok", "MODOK"],
    ["Batroc", "BatrocTheLeaper"],
    ["JoaquinTorres", "JoaquinTorresFalconIi"],
    ["MrSinister", "MisterSinister"],
    ["ThePhoenixForce", "PhoenixForce"],
    ["Gorr", "GorrTheGodButcher"],
    ["Infinaut", "TheInfinaut"],
    ["LivingTribunal", "TheLivingTribunal"],
    ["MBaku", "Mbaku"],
    ["DrDoom", "DoctorDoom"],
    ["M.o.d.o.k.", "Modok"],
    ["FallenOne", "TheFallenOne"],
    ["JaneFoster", "JaneFosterMightyThor"],
    ["ShouLao", "ShouLaoTheUndying"],
    ["Starlord", "StarLord"],
    ["Uatu", "UatuTheWatcher"],
    ["Viv", "VivVision"],
    ["MrFantasticFirstSteps", "MisterFantasticFirstSteps"],
  ]), []);

  // name -> id lookup
  const { nameToId } = useMemo(() => {
    const map = new Map();
    for (const c of snapCards) {
      const canon = toUpperCamel((c.cardname || "").trim());

      map.set(canon, c.id);

      // check if any alias should point to this ID
      for (const [alias, target] of ALIASES.entries()) {
        if (target === canon) {
          map.set(alias, c.id);
        }
      }
    }
    return { nameToId: map };
  }, [snapCards, ALIASES]);

// Build maps of existing decks
const existingByNameVersion = useMemo(() => {
  const m = new Map();
  for (const d of existingDecks) {
    // Handle both camelCase and DB lowercase
    const rawName = d.deckName || d.deckname || d.name || d.title || '(Unnamed Deck)';
    const version = d.versionNum || d.versionnum || "-";

    const combinedName =
      version && version !== "-"
        ? `${rawName} v${version}`
        : rawName;

    const { base } = splitDeckName(combinedName || "");
    const baseKey = normalizeName(base);
    const v = String(version || "-").toLowerCase();

    m.set(`${baseKey}|${v}`, d);
  }
  return m;
}, [existingDecks]);

const existingAnyVersionByName = useMemo(() => {
  const m = new Map();
  for (const d of existingDecks) {
    // Handle both camelCase and DB lowercase
    const rawName = d.deckName || d.deckname || d.name || d.title || '(Unnamed Deck)';
    const version = d.versionNum || d.versionnum || "-";

    const combinedName =
      version && version !== "-"
        ? `${rawName} v${version}`
        : rawName;

    const { base } = splitDeckName(combinedName || "");
    const baseKey = normalizeName(base);

    if (!m.has(baseKey)) m.set(baseKey, []);
    m.get(baseKey).push(d);
  }
  return m;
}, [existingDecks]);


  // Compute preview rows
  const computeRows = (inputDecks) => {
    const normalized = inputDecks.map((d, idx) => {
      const rawName = d.deckName || d.name || d.title || '';
      const { base, versionNum } = splitDeckName(rawName);
      const baseKey = normalizeName(base);
      const versionKey = String(versionNum || '-').toLowerCase();
      const cards = (d.cards || d.cardNames || d.list || []).slice(0, 12);
      const cardBackDefId = d.cardBackDefId || '';
      let derivedRank = d.rank ?? "The Rookies";
      const anyVerList = existingAnyVersionByName.get(baseKey);
      
      if (anyVerList && anyVerList.length > 0) {
        // If the deck already exists (Replace action), use its existing rank
        const existingRank = anyVerList[0].deckRank || anyVerList[0].rank || "The Rookies";
        derivedRank = existingRank;
      }

      // map names -> ids
      const ids = [];
      const missing = [];
      cards.forEach((n, i) => {
        const key = toUpperCamel((n || "").trim());
        const id = nameToId.get(key);
        if (id == null) {
          console.warn("MISSING:", nameToId);
          console.warn("MISSING:", n, "=>", key, "|");
          missing.push({ index: i, name: n, manualId: '' });
          ids.push(null);
        } else {
          ids.push(Number(id));
        }
      });
      while (ids.length < 12) {
        missing.push({ index: ids.length, name: '(missing)', manualId: '' });
        ids.push(null);
      }
      const finalIds = ids.slice(0, 12);

      // status logic
      const exact = existingByNameVersion.get(`${baseKey}|${versionKey}`);

      let status, action, targetId = null, error = '';

      if (exact) {
        console.log(idx + " => " + "Skip");
        status = 'skip';
        action = 'skip';
        targetId = exact.id ?? null;
      } else if (anyVerList && anyVerList.length > 0) {
        // Different version → replace existing
        console.log(idx + " => " + "Replace");
        status = 'replace';
        action = 'replace';
        // optional: pick the most recent or first existing deck id
        targetId = anyVerList[0].id ?? null;
      } else {
        console.log(idx + " => " + "Add");
        console.log(anyVerList);
        console.log(exact);
        console.log(existingByNameVersion);
        console.log(existingAnyVersionByName);
        status = 'add';
        action = 'add';
      }

      if (finalIds.some((v) => v == null) && action !== 'skip') {
        console.log(idx + " => " + "Error");
        status = 'error';
        error = 'Some cards could not be matched to IDs.';
      }

      // console.log("Row debug", {
      //   rawName,
      //   base,
      //   versionNum,
      //   baseKey,
      //   versionKey,
      //   exact: !!exact,
      //   anyVerList: anyVerList?.length
      // });
      return {
        idx,
        originalName: rawName,
        baseName: base,
        baseKey,
        versionNum,
        versionKey,
        cardNames: cards,
        cardIds: finalIds,
        cardBackDefId,
        rank: derivedRank,
        missing,
        status,
        action,
        targetId,
        error,
      };
    }).filter((row) => row.action !== 'skip');

    setRows(normalized);
  };

    function camelToWords(str) {
    if (!str) return "";
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")     // add space before capital letters
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2") // handle acronyms
      .trim();
  }

 function normalizeToDbFormat(str) {
    const merf = camelToWords(str)
      .replace("-", " ")
      .split(" ")
      .map((w, i) =>
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      )
      .join("")
      .replace("HawkeyeKateBishop", "KateBishop")
      .replace("JuggernautHorseman", "Juggernaut,HorsemanOfWar")
      .replace("SamWilsonCaptainAmerica", "SamWilson")
      .replace("DoctorDoom2099", "Doom2099")
      .replace("AranA", "Arana")
      .replace("MisterNegative", "MrNegative")
      .replace("OmegaSentinel", "KarimaShapandar")
      .replace("StarlordMasterOfTheSun", "StarLordMasterOfTheSun")
      .replace("ShouLaoTheUndying", "ShouLao")
      .replace("VivVision", "Viv")
      .replace("MisterFantastic", "MrFantastic")
      .replace("MODOK", "Modok")
      .replace("BatrocTheLeaper", "Batroc")
      .replace("RonanTheAccuser", "Ronan")
      .replace("TheHood", "Hood")
      .replace("Mbaku", "MBaku")
      .replace("TheLivingTribunal", "LivingTribunal")
      .replace("DoctorDoom", "DrDoom")
      .replace("PhoenixForce", "ThePhoenixForce")
      .replace("GorrTheGodButcher", "Gorr")
      .replace("TheInfinaut", "Infinaut")
      .replace("StarLord", "Starlord")
      .replace("MisterSinister", "MrSinister")
      .replace("TheFallenOne", "FallenOne")
      .replace("JaneFosterMightyThor", "JaneFoster")
      .replace("JoaquinTorresFalconIi", "JoaquinTorres")
      .replace("MisterFantasticFirstSteps", "MrFantasticFirstSteps")
      .replace("Modok", "M.o.d.o.k.")
      .replace("UatuTheWatcher", "Uatu");
    return merf;
  }

  // ------- Load cards + existing decks -------
  useEffect(() => {
    setLoadingCards(true);
    axios
      .get(ALL_CARDS_URL)
      .then((res) => {
        setSnapCards(res.data || []);
        console.log("Loaded card names:", (res.data || []).map(c => normalizeToDbFormat(c.cardname))); // log all card names
      })
      .catch((e) => log(`Failed to load cards: ${e?.message || e}`))
      .finally(() => setLoadingCards(false));

    setLoadingDecks(true);
    axios
      .get(ALL_DECKS_URL)
      .then((res) => setExistingDecks(res.data || []))
      .catch((e) => log(`Failed to load existing decks: ${e?.message || e}`))
      .finally(() => setLoadingDecks(false));

    axios
      .get(`${BASE_URL}api/data/snapDividors/${user.id}`)
      .then((res) => setUserRanks(res.data || []))
      .catch((e) => log(`Failed to load ranks: ${e?.message || e}`));
  }, []);

  // ------- Recompute rows -------
  useEffect(() => {
    if (pythonDecks.length && snapCards.length) {
      computeRows(pythonDecks);
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pythonDecks, snapCards, existingByNameVersion, existingAnyVersionByName]);

  // Prevent background scroll when panel is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    if (panelOpen) {
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      // Avoid layout shift when hiding scrollbar
      if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    } else {
      document.body.style.overflow = prev || '';
      document.body.style.paddingRight = prevPad || '';
    }
    return () => {
      document.body.style.overflow = prev || '';
      document.body.style.paddingRight = prevPad || '';
    };
  }, [panelOpen]);

  // ------- Actions (load) -------
  const openPanelAnd = (fn) => {
    if (!panelOpen) setPanelOpen(true);
    fn();
  };

  const loadFromEndpoint = async () => {
    setLoading(true);
    setLogs([]);
    try {
      const { data } = await axios.get(pythonUrl);
      const decks = coerceToDeckArray(data);
      if (!decks.length) {
        log('Endpoint returned no recognizable decks.');
        setPythonDecks([]);
        return;
      }
      setPythonDecks(decks);
      console.log(decks);
      log(`Loaded ${decks.length} deck(s) from endpoint.`);
    } catch (e) {
      log(`Error calling endpoint: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const loadFromFixedPath = async () => {
    setLoading(true);
    setLogs([]);
    try {
      const { data } = await axios.get(FROM_PATH_URL, { params: { path: DEFAULT_WINDOWS_PATH } });
      const decks = coerceToDeckArray(data);
      if (!decks.length) {
        log('Helper returned no recognizable decks.');
        setPythonDecks([]);
        return;
      }
      setPythonDecks(decks);
      log('Loaded deck(s) from fixed path.');
    } catch (e) {
      log(`Could not read fixed path via helper (${e?.message || e}). Falling back to file picker…`);
      fileInputRef.current?.click();
    } finally {
      setLoading(false);
    }
  };

  const RANK_TO_CARD_BACK_ID = {
    "Contenders for Infinite": 296,
    "Testing Grounds": 212,
    "Noteworthy Decks": 213,
    "Dropped": 148,
    "The Rookies": 212,
    "Upcoming Decks": 148,
  };

  const getSnapCardBackIdForRank = (rank) => {
    return RANK_TO_CARD_BACK_ID[rank] ?? 212; // default fallback (same as The Rookies)
  };

  const loadFromFilePicker = async (file) => {
    try {
      const text = await file.text();
      const json = JSON.parse(stripBOM(text));
      const decks = coerceToDeckArray(json);
      if (!decks.length) {
        log('Selected file parsed, but no recognizable decks found.');
        setPythonDecks([]);
        return;
      }
      setPythonDecks(decks);
      console.log(decks);
      log(`Loaded ${decks.length} deck(s) from file: ${file.name}`);
    } catch (e) {
      log(`Failed to parse file: ${e?.message || e}`);
    }
  };

  // ------- Manual fixes -------
  const updateManualId = (rowIdx, missIndex, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r;
        const missing = r.missing.map((m, j) => (j === missIndex ? { ...m, manualId: value } : m));
        return { ...r, missing };
      })
    );
  };

  const applyManualFixes = (rowIdx) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r;
        const nextIds = [...r.cardIds];
        let stillMissing = [];
        for (const m of r.missing) {
          const id = Number(m.manualId);
          if (!Number.isFinite(id) || id <= 0) {
            stillMissing.push(m);
          } else {
            nextIds[m.index] = id;
          }
        }
        let status = r.status;
        let error = '';
        if (nextIds.some((v) => v == null) && r.action !== 'skip') {
          status = 'error';
          error = 'Some cards could not be matched to IDs.';
        } else {
          status = r.action;
        }
        return { ...r, cardIds: nextIds, missing: stillMissing, status, error };
      })
    );
  };

  // ------- Import/Replace helpers -------
  const buildPayload = (r) => {
    const rank = r.rank || "The Rookies";
    const snapCardBackId = getSnapCardBackIdForRank(rank);

    return {
      deckName: r.baseName,
      userId: user.id,
      versionNum: r.versionNum || "-",
      monthCompleted: "-",
      deckRank: rank,
      snapCardBackId,

      card1: Number(r.cardIds[0]) || 0,
      card2: Number(r.cardIds[1]) || 0,
      card3: Number(r.cardIds[2]) || 0,
      card4: Number(r.cardIds[3]) || 0,
      card5: Number(r.cardIds[4]) || 0,
      card6: Number(r.cardIds[5]) || 0,
      card7: Number(r.cardIds[6]) || 0,
      card8: Number(r.cardIds[7]) || 0,
      card9: Number(r.cardIds[8]) || 0,
      card10: Number(r.cardIds[9]) || 0,
      card11: Number(r.cardIds[10]) || 0,
      card12: Number(r.cardIds[11]) || 0,
    };
  };

  const doAdd = async (r) => {
    const payload = buildPayload(r);
    const { data } = await axios.post(ADD_DECK_URL, payload);
    return data;
  };

  const doReplace = async (r) => {
    if (!r.targetId) throw new Error('Missing target deck id to replace.');
    const payload = { id: r.targetId, ...buildPayload(r) };
    try {
      const { data } = await axios.put(UPDATE_DECK_PUT(r.targetId), payload);
      return data;
    } catch {
      const { data } = await axios.post(UPDATE_DECK_POST, payload);
      return data;
    }
  };

  const importOne = async (rowIdx) => {
    const r = rows[rowIdx];
    if (!r) return;
    if (r.action === 'skip') {
      log(`Skipped "${r.baseName}" (${r.versionNum}) — same name+version already in DB.`);
      setRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, status: 'skip' } : x)));
      return;
    }
    if (r.status === 'error') return;

    setRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, status: 'importing' } : x)));
    try {
      let data;
      if (r.action === 'replace') {
        data = await doReplace(r);
        log(`Replaced "${r.baseName}" → id ${data?.id ?? r.targetId ?? 'unknown'}`);
        setExistingDecks((prev) =>
          prev.map((d) =>
            d.id === r.targetId
              ? { ...d, deckName: r.baseName, versionNum: r.versionNum, deckRank: r.rank }
              : d
          )
        );
      } else {
        data = await doAdd(r);
        log(`Added "${r.baseName}" (${r.versionNum}) → id ${data?.id ?? 'unknown'}`);
        setExistingDecks((prev) => [
          ...prev,
          { id: data?.id ?? Math.random(), deckName: r.baseName, versionNum: r.versionNum },
        ]);
      }
      setRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, status: 'imported', error: '' } : x)));
    } catch (e) {
      const msg = e?.response?.data?.details || e?.response?.data?.error || e?.message || String(e);
      log(`❌ ${r.action === 'replace' ? 'Replace' : 'Import'} failed for "${r.baseName}": ${msg}`);
      setRows((prev) =>
        prev.map((x, i) => (i === rowIdx ? { ...x, status: 'error', error: `${r.action === 'replace' ? 'Replace' : 'Import'} failed: ${msg}` } : x))
      );
    }
  };

  const importAllValid = async () => {
    const candidates = rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.status === r.action && (r.action === 'add' || r.action === 'replace'));
    if (!candidates.length) { log('No decks are ready to add/replace.'); return; }
    setImportingAll(true);
    for (const { i } of candidates) { await importOne(i); }
    setImportingAll(false);
  };

  // ------- UI -------
  const busy = loading || loadingCards || loadingDecks || importingAll;

  return (
    <div style={{ background: '#0b0e14', color: '#eaeaea' }}>
      {/* Only top row of buttons */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'linear-gradient(180deg, #0b0e14 0%, rgba(11,14,20,0.85) 100%)',
        borderBottom: '1px solid #1b2333',
        padding: 12
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'center' }}>
          <input
            type="url"
            value={pythonUrl}
            onChange={(e) => setPythonUrl(e.target.value)}
            placeholder="Endpoint returning decks JSON or raw CollectionState.json"
            style={inputTop}
          />
          <button
            onClick={() => openPanelAnd(loadFromEndpoint)}
            disabled={busy}
            style={cta('#4fc3f7', '#0288d1', '#145b82', busy)}
            title="Fetch decks from endpoint and preview"
          >
            {loading ? 'Loading…' : 'Load from Endpoint'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) loadFromFilePicker(f);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => openPanelAnd(loadFromFixedPath)}
            disabled={busy}
            style={cta('#ffd54f', '#ffb300', '#8a6a00', busy)}
            title="Read fixed Windows path via local helper (falls back to picker on error)"
          >
            Load from File
          </button>
          {/* <button
            onClick={() => { if (!panelOpen) setPanelOpen(true); importAllValid(); }}
            disabled={busy || !rows.some((r) => r.status === r.action && (r.action === 'add' || r.action === 'replace'))}
            style={cta('#9be15d', '#00e3ae', '#0c8a72', busy)}
            title="Import all adds/replacements that are ready"
          >
            {importingAll ? 'Processing…' : 'Process All Ready'}
          </button> */}
        </div>
      </div>

      {/* FULL-SCREEN OVERLAY (captures all scroll) */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 4000,                // 🔥 above Navbar (3000) and everything else
          pointerEvents: panelOpen ? 'auto' : 'none',
          background: panelOpen ? 'rgba(6, 10, 18, 0.72)' : 'transparent',
          transition: 'background 220ms ease',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
        aria-hidden={!panelOpen}
      >
        {/* Slide panel */}
        <div
          style={{
            transform: panelOpen ? 'translateY(0)' : 'translateY(-16px)',
            opacity: panelOpen ? 1 : 0,
            transition: 'transform 260ms ease, opacity 260ms ease',
            marginTop: 12,
            width: 'min(1200px, calc(100% - 24px))',
            maxHeight: 'calc(100% - 24px)',

            /* make the panel itself scroll */
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',

            border: '1px solid #1f2a3e',
            borderRadius: 14,
            background: 'linear-gradient(180deg, rgba(16,21,33,0.98), rgba(14,18,28,0.98))',
            boxShadow: '0 24px 72px rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
          }}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 14px', background: 'linear-gradient(180deg,#0f1626,#0c1220)',
            position: 'sticky', top: 0, zIndex: 1
          }}>
            <div style={{ fontWeight: 800, letterSpacing: 0.3 }}>Preview & Import</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#9bb0d3' }}>
                Cards: {loadingCards ? 'loading…' : snapCards.length} • Existing: {loadingDecks ? 'loading…' : existingDecks.length} • Loaded: {pythonDecks.length}
              </span>
              <button onClick={() => setPanelOpen(false)} style={ghostBtn}>Close</button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div style={{ overflow: 'auto', padding: 14, maxHeight: 'calc(100% - 50px)' }}>
            {/* Table */}
            {!!rows.length ? (
              <div style={{ border: '1px solid #1f2a3e', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead style={{ background: '#121a2b', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={th}>Deck Name</th>
                      <th style={th}>Version</th>
                      <th style={th}>Card Back</th>
                      <th style={th}>Derived Rank</th>
                      <th style={th}>Action</th>
                      <th style={th}>Missing / Fix</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #1f2a3e' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 700, color: '#fff' }}>{r.baseName}</div>
                          {/* <div style={{ color: '#9bb0d3', fontSize: 12 }}>{r.originalName}</div> */}
                        </td>
                        <td style={td} width={90}>{r.versionNum}</td>
                        <td style={td} width={160}>{r.cardBackDefId || <span style={{ color: '#777' }}>—</span>}</td>
                        <td style={td} width={140}>
                          <select
                            value={r.rank}
                            onChange={(e) => {
                              const newRank = e.target.value;
                              setRows((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, rank: newRank } : x))
                              );
                            }}
                            style={{
                              background: '#0e1422',
                              border: '1px solid #2a3a55',
                              borderRadius: 8,
                              color: '#e9eef9',
                              padding: '6px 8px',
                              fontWeight: 600,
                            }}
                          >
                            <option value="The Rookies">The Rookies</option>
                            {userRanks.map((rnk) => (
                              <option key={rnk.id} value={rnk.dividor}>
                                {rnk.dividor}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={td} width={180}>
                          {r.status === 'add' && <span style={pill('#1b5e20', '#9be15d')}>Add</span>}
                          {r.status === 'replace' && <span style={pill('#0d47a1', '#64e1ff')}>Replace</span>}
                          {r.status === 'skip' && <span style={pill('#424242', '#bdbdbd')}>Skip (same version)</span>}
                          {r.status === 'importing' && <span style={pill('#0d47a1', '#4fc3f7')}>Processing…</span>}
                          {r.status === 'imported' && <span style={pill('#004d40', '#00e3ae')}>Done</span>}
                          {r.status === 'error' && (
                            <div>
                              <span style={pill('#b71c1c', '#ef9a9a')}>Error</span>
                              <div style={{ color: '#ff9e9e', fontSize: 12, marginTop: 4 }}>{r.error}</div>
                            </div>
                          )}
                        </td>
                        <td style={td}>
                          {r.missing.length === 0 || r.action === 'skip' ? (
                            <span style={{ color: '#9be15d' }}>{r.action === 'skip' ? 'N/A' : 'None'}</span>
                          ) : (
                            <div style={{ display: 'grid', gap: 6 }}>
                              {r.missing.map((m, mi) => (
                                <div
                                  key={mi}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 110px',
                                    gap: 8,
                                    alignItems: 'center',
                                  }}
                                >
                                  <div style={{ color: '#ffd89c' }}>
                                    Card {m.index + 1}: <em>{m.name}</em>
                                  </div>
                                  <input
                                    type="number"
                                    placeholder="card id"
                                    value={m.manualId}
                                    onChange={(e) => updateManualId(idx, mi, e.target.value)}
                                    style={inputField}
                                  />
                                </div>
                              ))}
                              <button
                                onClick={() => applyManualFixes(idx)}
                                style={{ ...ghostBtn, borderColor: '#25324a' }}
                              >
                                Apply Fixes
                              </button>
                            </div>
                          )}
                        </td>
                        <td style={td} width={200}>
                          <div style={{ display: 'grid', gap: 6 }}>
                            <button
                              onClick={() => importOne(idx)}
                              disabled={
                                busy ||
                                r.action === 'skip' ||
                                r.status === 'error' ||
                                (r.action !== 'skip' && r.cardIds.some((v) => v == null))
                              }
                              style={{
                                ...buttonBase,
                                background:
                                  r.action === 'replace'
                                    ? 'linear-gradient(135deg, #64e1ff, #0d47a1)'
                                    : 'linear-gradient(135deg, #9be15d, #00e3ae)',
                                border: '1px solid #145b82',
                                color: '#051018',
                                opacity:
                                  busy ||
                                  r.action === 'skip' ||
                                  r.status === 'error' ||
                                  (r.action !== 'skip' && r.cardIds.some((v) => v == null))
                                    ? 0.6
                                    : 1,
                              }}
                            >
                              {r.action === 'replace' ? 'Replace in DB' : r.action === 'skip' ? 'Skip' : 'Add to DB'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: '#9bb0d3', padding: 12 }}>{loading ? 'Loading…' : 'No decks loaded yet.'}</div>
            )}

            {/* Logs */}
            <div style={{ border: '1px solid #1f2a3e', borderRadius: 10, padding: 10, background: '#0f1626' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Log</div>
              <div style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12 }}>
                {logs.length === 0 ? (
                  <div style={{ color: '#7f91b3' }}>No messages yet.</div>
                ) : (
                  logs.map((l, i) => <div key={i}>• {l}</div>)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- SNAP THEME STYLES ---------- */

const th = {
  textAlign: 'left',
  padding: '10px 12px',
  color: 'var(--snap-gold)',
  fontWeight: 700,
  borderBottom: '1px solid var(--snap-line)',
  background: 'var(--snap-panel)',
  textShadow: '0 0 6px rgba(197,176,55,0.4)',
};

const td = {
  padding: '10px 12px',
  verticalAlign: 'top',
  color: 'var(--snap-text)',
};

const inputTop = {
  width: '100%',
  background: 'var(--snap-panel)',
  border: '2px solid var(--snap-gold)',
  borderRadius: 10,
  color: 'var(--snap-gold)',
  padding: '10px 12px',
  outline: 'none',
  fontWeight: 600,
};

const inputField = {
  width: '100%',
  background: 'var(--snap-panel)',
  border: '1px solid var(--snap-gold)',
  borderRadius: 8,
  color: 'var(--snap-gold)',
  padding: '8px 10px',
  outline: 'none',
};

const buttonBase = {
  borderRadius: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  fontWeight: 800,
  letterSpacing: 0.3,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

const cta = (c1, c2, border, disabled) => ({
  ...buttonBase,
  background: `linear-gradient(135deg, ${c1}, ${c2})`,
  border: `2px solid var(--snap-gold)`,
  color: 'black',
  opacity: disabled ? 0.6 : 1,
  boxShadow: disabled ? 'none' : '0 0 12px rgba(197,176,55,0.5)',
});

const ghostBtn = {
  ...buttonBase,
  background: 'transparent',
  border: '2px solid var(--snap-gold)',
  color: 'var(--snap-gold)',
};

const pill = (borderColor, bg) => ({
  display: 'inline-block',
  border: `1px solid ${borderColor}`,
  background: bg,
  color: '#000',
  padding: '2px 10px',
  borderRadius: 999,
  fontWeight: 800,
  boxShadow: '0 0 8px rgba(197,176,55,0.3)',
});
